import { ChangeEvent, EventHandler, KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ActivitySubTag as ActivitySubTagType } from '@percent/admin-dashboard/api/types'
import { NoResultTable } from '@percent/admin-dashboard/common/components'
import { ActivitySubTag as ActivitySubTagComponent } from '@percent/admin-dashboard/common/components/activitySubTag/ActivitySubTag'
import { useActivityTags } from '@percent/admin-dashboard/common/hooks/useActivityTags/useActivityTags'
import { DecoratedActivitySubTag } from '@percent/admin-dashboard/common/hooks/useActivityTags/useActivityTags.types'
import {
  usePostHogFeatureFlag,
  POSTHOG_FEATURE_FLAGS
} from '@percent/admin-dashboard/common/hooks/usePostHogFeatureFlag'
import { sortEducationSubtags } from '@percent/admin-dashboard/common/utility/sortEducationSubtags'
import { Accordion, Checkbox, SearchInput, Typography } from '@percent/lemonade'
import styles from './ActivityTags.module.scss'
import { ActivityTagsProps } from './ActivityTags.types'

export function ActivityTags({
  subTagsForm,
  onSubTagChange,
  isFlagged,
  isOnlyEducationOrganisation,
  onEducationSubTagChange,
  isNoneOfTheAbove,
  setIsNoneOfTheAbove,
  setNotDeprecatedSubTags
}: Readonly<ActivityTagsProps>) {
  const [hasOnlyEduStageSubTags, setHasOnlyEduStageSubTags] = useState(subTagsForm.length === 0)
  const handleNewEducationSubTagsFlow = isOnlyEducationOrganisation && hasOnlyEduStageSubTags
  const { activityTags, setSearchActivityTagsValue, mapHitsToTags, isActivityTagsDataFiltered } = useActivityTags({
    filterByEducation: handleNewEducationSubTagsFlow
  })

  const educationSubtagsNoneOfTheAboveFeatureFlag = usePostHogFeatureFlag(
    POSTHOG_FEATURE_FLAGS.AD_NONE_OF_THE_ABOVE_EDU_SUBTAGS
  )
  const { t } = useTranslation()
  const [subTagsFormInitialValue] = useState(subTagsForm)
  const [searchValue, setSearchValue] = useState('')
  const [accordionValue, setAccordionValue] = useState<string[]>([])
  const [activitySubTags, setActivitySubTags] = useState<DecoratedActivitySubTag[]>([])
  const [filteredActivitySubTags, setFilteredActivitySubTags] = useState<DecoratedActivitySubTag[]>([])
  const [deprecatedSubtags, setDeprecatedSubtags] = useState<ActivitySubTagType[]>([])

  const tags = mapHitsToTags(filteredActivitySubTags)
  const initalAccordionSetRef = useRef<string[] | null>(null)

  useEffect(() => {
    if (tags.length <= 0 || initalAccordionSetRef.current) return
    const initialTagCategories = subTagsFormInitialValue
      .map(subTag => tags.find(tag => tag.subTags.some(sub => sub.id === subTag.id))?.name)
      .filter(Boolean) as string[]

    setAccordionValue(initialTagCategories)
    initalAccordionSetRef.current = initialTagCategories
  }, [subTagsFormInitialValue, tags])

  useEffect(() => {
    if (isActivityTagsDataFiltered) {
      setAccordionValue(tags.map(tag => tag.name))
    } else {
      setAccordionValue(initalAccordionSetRef.current ?? [])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActivityTagsDataFiltered])

  useEffect(
    () =>
      setActivitySubTags(
        activityTags?.flatMap(({ id, name, subTags }) =>
          subTags.map(subTag => ({ ...subTag, parentTagName: name, parentTagId: id }))
        )
      ),
    [activityTags]
  )

  useEffect(() => {
    if (subTagsFormInitialValue.length > 0) {
      setHasOnlyEduStageSubTags(
        subTagsFormInitialValue.every(
          initialSubTag => activitySubTags.find(s => s.id === initialSubTag.id)?.isEducationStage
        )
      )
    }

    const filteredSubTags = activitySubTags.filter(
      subTag =>
        !subTag.deprecatedAt ||
        (!!subTag.deprecatedAt && subTagsFormInitialValue.findIndex(s => s.id === subTag.id) !== -1)
    )

    setDeprecatedSubtags(filteredSubTags.filter(subTag => !!subTag.deprecatedAt))
    setNotDeprecatedSubTags(subTagsForm.filter(subTag => !filteredSubTags.find(s => s.id === subTag.id)?.deprecatedAt))

    if (handleNewEducationSubTagsFlow) {
      setFilteredActivitySubTags([...filteredSubTags].sort(sortEducationSubtags))
    } else {
      setFilteredActivitySubTags(filteredSubTags)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activitySubTags, handleNewEducationSubTagsFlow, subTagsFormInitialValue])

  useEffect(() => {
    if (subTagsFormInitialValue.length > 0 && subTagsFormInitialValue.every(subTag => subTag.meetsCriteria === false)) {
      setIsNoneOfTheAbove(true)
    }
  }, [subTagsFormInitialValue, setIsNoneOfTheAbove])

  const isSubTagSelected = (subTagId: string) =>
    subTagsForm.find(subTag => subTag.id === subTagId)?.meetsCriteria ||
    deprecatedSubtags.findIndex(subTag => subTag.id === subTagId) !== -1 ||
    false

  const handleKeyChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value)
  }

  const onSubTagValueChange = (subTagId: string) => {
    if (handleNewEducationSubTagsFlow) {
      const notDeprecatedHits = activitySubTags.filter(subTag => !subTag.deprecatedAt)

      const educationSubTags =
        subTagsForm.length > 0
          ? notDeprecatedHits.map(subTag => ({
              id: subTag.id,
              meetsCriteria: subTagsForm.find(({ id }) => id === subTag.id)?.meetsCriteria || false
            }))
          : notDeprecatedHits.map(subTag => ({ id: subTag.id, meetsCriteria: false }))

      onEducationSubTagChange(educationSubTags, subTagId)
    } else {
      onSubTagChange(subTagId)
    }
  }

  const onNoneOfAboveChange = () => {
    setIsNoneOfTheAbove(!isNoneOfTheAbove)
    onEducationSubTagChange(
      activitySubTags.filter(subTag => !subTag.deprecatedAt).map(subTag => ({ id: subTag.id, meetsCriteria: false }))
    )
  }

  const handleKeyPress: EventHandler<KeyboardEvent<HTMLInputElement>> = useCallback(
    event => {
      if (event.key === 'Enter') {
        setSearchValue((event.target as HTMLInputElement).value)
      }

      if (event.key === 'Escape') {
        setSearchValue('')
      }

      return null
    },
    [setSearchValue]
  )

  const handleClearValue = () => {
    setSearchValue('')
  }

  useEffect(() => {
    setSearchActivityTagsValue(searchValue)
  }, [searchValue, setSearchActivityTagsValue])

  return (
    <div className={styles.container} data-testid="eligibility-activity-tags">
      {!handleNewEducationSubTagsFlow && (
        <SearchInput
          value={searchValue ?? ''}
          handleClearValue={handleClearValue}
          onKeyDown={handleKeyPress}
          onChange={handleKeyChange}
          placeholder={t('typography.findATag')}
          disabled={!activityTags.length && !searchValue}
        />
      )}
      <Typography variant="h3" className={styles.title}>
        {t('typography.parentTagsAndSubtags')}
      </Typography>
      {tags?.length === 0 ? (
        <div className={styles.noResults}>
          <NoResultTable text={t('typography.noResultsFound')} type="default" />
        </div>
      ) : (
        <Accordion
          classNames={{
            control: styles.accordionControl,
            item: styles.accordionItem
          }}
          multiple
          value={accordionValue}
          onChange={setAccordionValue}
        >
          {tags?.map(tag => (
            <Accordion.Item key={tag.id} value={tag.name}>
              <Accordion.Control>
                <Typography variant="default">{tag.name}</Typography>
              </Accordion.Control>
              <Accordion.Panel>
                <>
                  <div className={styles.subTagsContainer}>
                    {tag.subTags.map((subTag, index) => (
                      <ActivitySubTagComponent
                        key={subTag.id}
                        active={isSubTagSelected(subTag.id)}
                        disabled={
                          isFlagged ||
                          isNoneOfTheAbove ||
                          !!deprecatedSubtags.find(s => s.id === subTag.id)?.deprecatedAt
                        }
                        subTag={subTag}
                        searchWords={subTag.highlightedWords || []}
                        index={index}
                        onChange={onSubTagValueChange}
                      />
                    ))}
                  </div>
                  {educationSubtagsNoneOfTheAboveFeatureFlag && handleNewEducationSubTagsFlow && (
                    <li>
                      <div className={styles.tagTitle}>
                        <p>{t('typography.noneOfTheAbove').toUpperCase()}</p>
                      </div>
                      <div className={styles.subTagsContainer}>
                        <Checkbox
                          variant="default"
                          name="nonOfAbove"
                          value="nonOfAbove"
                          onChange={onNoneOfAboveChange}
                          disabled={isFlagged}
                          active={isNoneOfTheAbove}
                          label={t('typography.noneOfTheAboveMatches')}
                        />
                      </div>
                    </li>
                  )}
                </>
              </Accordion.Panel>
            </Accordion.Item>
          ))}
        </Accordion>
      )}
    </div>
  )
}
